import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { Progress } from "@/components/ui/progress";
import { Send, Save, TestTube2, Users, ArrowLeft, Calendar, CheckCircle2, XCircle } from "lucide-react";

const VARIABLES = [
  { key: "first_name", label: "Nome" },
  { key: "last_name", label: "Cognome" },
  { key: "email", label: "Email" },
  { key: "company", label: "Azienda" },
  { key: "company_name", label: "Nome mittente" },
  { key: "unsubscribe_url", label: "Link disiscrivi" },
];

function personalizePreview(html, contact) {
  const ctx = {
    first_name: contact.first_name || "Mario",
    last_name: contact.last_name || "Rossi",
    email: contact.email || "mario.rossi@example.com",
    company: contact.company || "Acme SRL",
    company_name: contact.company_name || "La tua azienda",
    unsubscribe_url: "#unsubscribe",
    month: new Date().toLocaleString("it-IT", { month: "long" }),
  };
  return (html || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) =>
    ctx[k] !== undefined ? ctx[k] : `{{${k}}}`
  );
}

export default function CampaignEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id;

  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    name: "",
    subject: "",
    html: "<p>Ciao {{first_name}},</p><p>Scrivi qui il contenuto…</p>",
    from_name: "",
    reply_to: "",
    list_ids: [],
    scheduled_at: "",
    track_opens: true,
    track_clicks: true,
  });
  const [recipientCount, setRecipientCount] = useState(0);
  const [status, setStatus] = useState("draft");
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleValue, setScheduleValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [resendStep, setResendStep] = useState(0); // 0 closed · 1 first confirm · 2 second confirm
  const [prevSendInfo, setPrevSendInfo] = useState({ sent_at: null, recipients: 0, sent: 0 });

  useEffect(() => {
    api.get("/lists").then(({ data }) => setLists(data));
    api.get("/templates").then(({ data }) => setTemplates(data));

    if (isNew) {
      const prefill = sessionStorage.getItem("nl_prefill_campaign");
      if (prefill) {
        try {
          const p = JSON.parse(prefill);
          setForm((f) => ({ ...f, ...p }));
        } catch {}
        sessionStorage.removeItem("nl_prefill_campaign");
      }
    } else {
      api.get(`/campaigns/${id}`).then(({ data }) => {
        setForm({
          name: data.name || "",
          subject: data.subject || "",
          html: data.html || "",
          from_name: data.from_name || "",
          reply_to: data.reply_to || "",
          list_ids: data.list_ids || [],
          scheduled_at: data.scheduled_at || "",
          track_opens: data.track_opens ?? true,
          track_clicks: data.track_clicks ?? true,
        });
        setStatus(data.status);
        setPrevSendInfo({
          sent_at: data.sent_at || null,
          recipients: (data.stats || {}).recipients || 0,
          sent: (data.stats || {}).sent || 0,
        });
      });
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/campaigns/${id}/recipients-count`).then(({ data }) => {
      setRecipientCount(data.count);
    });
  }, [id, form.list_ids]);

  const preview = useMemo(
    () =>
      personalizePreview(form.html, {
        first_name: "Mario",
        last_name: "Rossi",
        email: "mario.rossi@example.com",
        company: "Acme SRL",
        company_name: form.from_name || "La tua azienda",
      }),
    [form.html, form.from_name]
  );

  const saveCampaign = async (opts = {}) => {
    setSaving(true);
    try {
      let campaignId = id;
      if (isNew) {
        const { data } = await api.post("/campaigns", form);
        campaignId = data.id;
        navigate(`/campagne/${data.id}`, { replace: true });
      } else {
        await api.patch(`/campaigns/${id}`, form);
      }
      if (opts.silent !== true) toast.success("Campagna salvata");
      return campaignId;
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error("Inserisci un'email di test");
    if (!user?.microsoft_connected) {
      return toast.error("Collega prima il tuo account Microsoft 365 nelle Impostazioni");
    }
    const cid = await saveCampaign({ silent: true });
    if (!cid) return;
    try {
      await api.post(`/campaigns/${cid}/send?test_email=${encodeURIComponent(testEmail)}`);
      toast.success(`Email di test inviata a ${testEmail}`);
      setTestOpen(false);
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore invio test");
    }
  };

  const sendNow = async () => {
    if (!user?.microsoft_connected) {
      return toast.error("Collega prima il tuo account Microsoft 365 nelle Impostazioni");
    }
    if (!form.list_ids?.length) {
      return toast.error("Seleziona almeno una lista di destinatari");
    }
    if (!window.confirm(`Inviare la campagna a ${recipientCount} destinatari?`)) return;
    const cid = await saveCampaign({ silent: true });
    if (!cid) return;
    try {
      const { data } = await api.post(`/campaigns/${cid}/send`);
      if (data.queued) {
        setSendOpen(true);
        setProgress({
          status: "sending",
          total: data.total_recipients,
          sent: 0,
          failed: 0,
          processed: 0,
          percent: 0,
        });
      }
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore invio");
    }
  };

  const doResendCampaign = async () => {
    // Called after both confirmation dialogs
    setResendStep(0);
    try {
      const { data } = await api.post(`/campaigns/${id}/send?force_resend=true`);
      if (data.queued) {
        setSendOpen(true);
        setProgress({
          status: "sending",
          total: data.total_recipients,
          sent: 0,
          failed: 0,
          processed: 0,
          percent: 0,
        });
      }
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore reinvio");
    }
  };

  // Poll progress while send dialog is open
  useEffect(() => {
    if (!sendOpen) return;
    const cid = id;
    if (!cid) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/campaigns/${cid}/progress`);
        if (cancelled) return;
        setProgress(data);
        if (data.status === "sent" || data.status === "failed") return true;
      } catch (err) {
        // keep polling
      }
      return false;
    };
    const interval = setInterval(async () => {
      const done = await tick();
      if (done) clearInterval(interval);
    }, 2000);
    // First immediate tick
    tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [sendOpen, id]);

  const schedule = async () => {
    if (!scheduleValue) return toast.error("Scegli data e ora");
    if (!form.list_ids?.length) {
      setScheduleOpen(false);
      return toast.error("Seleziona almeno una lista di destinatari");
    }
    const iso = new Date(scheduleValue).toISOString();
    setForm({ ...form, scheduled_at: iso });
    const cid = await saveCampaign({ silent: true });
    if (!cid) return;
    await api.patch(`/campaigns/${cid}`, { ...form, scheduled_at: iso });
    toast.success("Campagna programmata");
    setScheduleOpen(false);
    navigate("/campagne");
  };

  const toggleList = (lid) => {
    setForm((f) => ({
      ...f,
      list_ids: f.list_ids.includes(lid)
        ? f.list_ids.filter((x) => x !== lid)
        : [...f.list_ids, lid],
    }));
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({ ...f, subject: t.subject, html: t.html }));
    toast.success(`Template "${t.name}" applicato`);
  };

  const insertVar = (k) => {
    setForm((f) => ({ ...f, html: (f.html || "") + `{{${k}}}` }));
  };

  const disabled = status === "sent";

  return (
    <>
      <PageHeader
        title={isNew ? "Nuova campagna" : form.name || "Campagna"}
        subtitle={
          disabled
            ? "Campagna già inviata (sola lettura)."
            : `${recipientCount} destinatari selezionati`
        }
        testid="editor-header"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/campagne")} data-testid="back-campaigns">
              <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
            </Button>
            {!disabled && (
              <>
                <Button variant="outline" onClick={() => saveCampaign()} disabled={saving} data-testid="save-campaign">
                  <Save className="w-4 h-4 mr-2" /> Salva bozza
                </Button>
                <Button variant="outline" onClick={() => setTestOpen(true)} data-testid="send-test-btn">
                  <TestTube2 className="w-4 h-4 mr-2" /> Test
                </Button>
                <Button variant="outline" onClick={() => setScheduleOpen(true)} data-testid="schedule-btn">
                  <Calendar className="w-4 h-4 mr-2" /> Programma
                </Button>
                <Button onClick={sendNow} data-testid="send-now-btn">
                  <Send className="w-4 h-4 mr-2" /> Invia ora
                </Button>
              </>
            )}
            {disabled && (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/campagne/${id}/report`)}
                  data-testid="view-report-btn"
                >
                  Vedi report
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setResendStep(1)}
                  data-testid="resend-btn"
                >
                  <Send className="w-4 h-4 mr-2" /> Rimanda campagna
                </Button>
              </>
            )}
          </>
        }
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: config + editor */}
          <div className="space-y-4">
            <div className="border border-border bg-white p-6 space-y-4">
              <div>
                <Label>Nome interno della campagna</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Es. Newsletter Marzo 2026"
                  disabled={disabled}
                  data-testid="campaign-name"
                />
              </div>
              <div>
                <Label>Oggetto email</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Ciao {{first_name}}, ecco le novità"
                  disabled={disabled}
                  data-testid="campaign-subject"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome mittente (facoltativo)</Label>
                  <Input
                    value={form.from_name}
                    onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                    placeholder="Es. La Tua Azienda"
                    disabled={disabled}
                    data-testid="campaign-from-name"
                  />
                </div>
                <div>
                  <Label>Reply-to (facoltativo)</Label>
                  <Input
                    type="email"
                    value={form.reply_to}
                    onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
                    placeholder="risposte@azienda.it"
                    disabled={disabled}
                    data-testid="campaign-reply-to"
                  />
                </div>
              </div>
            </div>

            <div className="border border-border bg-white p-6 space-y-3">
              <div className="flex items-center justify-between">
                <Label>Destinatari — Liste</Label>
                <span className="text-xs text-muted-foreground">
                  <Users className="inline w-3 h-3 mr-1" />
                  {recipientCount} contatti
                </span>
              </div>
              {lists.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  Nessuna lista disponibile.{" "}
                  <button
                    className="text-primary underline"
                    onClick={() => navigate("/liste")}
                  >
                    Creane una
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {lists.map((l) => (
                  <label
                    key={l.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={form.list_ids.includes(l.id)}
                      onCheckedChange={() => toggleList(l.id)}
                      disabled={disabled}
                      data-testid={`toggle-list-${l.id}`}
                    />
                    <span className="flex-1">{l.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {l.contact_count}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border border-border bg-white p-6 space-y-3">
              <Label>Applica un template</Label>
              <Select onValueChange={applyTemplate} disabled={disabled}>
                <SelectTrigger data-testid="apply-template-select">
                  <SelectValue placeholder="Scegli un template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                  {templates.length === 0 && (
                    <div className="p-2 text-sm text-muted-foreground">
                      Nessun template. Vai a Template per crearne.
                    </div>
                  )}
                </SelectContent>
              </Select>
              <div>
                <Label>Variabili disponibili</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {VARIABLES.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      className="text-xs px-2 py-1 border border-border rounded-sm bg-muted/50 hover:bg-primary hover:text-primary-foreground transition-colors"
                      onClick={() => insertVar(v.key)}
                      disabled={disabled}
                      data-testid={`var-${v.key}`}
                    >
                      {`{{${v.key}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="border border-border bg-white p-6 space-y-3">
              <Label>Tracciamento</Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.track_opens}
                  onCheckedChange={(v) => setForm({ ...form, track_opens: !!v })}
                  disabled={disabled}
                  data-testid="track-opens"
                />
                Traccia le aperture (pixel invisibile)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.track_clicks}
                  onCheckedChange={(v) => setForm({ ...form, track_clicks: !!v })}
                  disabled={disabled}
                  data-testid="track-clicks"
                />
                Traccia i click sui link
              </label>
            </div>
          </div>

          {/* RIGHT: editor + preview */}
          <div className="border border-border bg-white flex flex-col min-h-[600px]">
            <Tabs defaultValue="edit" className="flex-1 flex flex-col">
              <TabsList className="border-b border-border rounded-none justify-start w-full bg-transparent px-4">
                <TabsTrigger value="edit" data-testid="tab-edit">
                  HTML
                </TabsTrigger>
                <TabsTrigger value="preview" data-testid="tab-preview">
                  Anteprima
                </TabsTrigger>
              </TabsList>
              <TabsContent value="edit" className="flex-1 p-0 m-0">
                <Textarea
                  value={form.html}
                  onChange={(e) => setForm({ ...form, html: e.target.value })}
                  className="editor-code border-0 rounded-none h-full min-h-[560px] resize-none focus-visible:ring-0"
                  disabled={disabled}
                  data-testid="campaign-html"
                />
              </TabsContent>
              <TabsContent value="preview" className="flex-1 p-0 m-0">
                <iframe
                  title="preview"
                  srcDoc={preview}
                  className="w-full h-full min-h-[560px] bg-white"
                  data-testid="preview-iframe"
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </PageBody>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent data-testid="test-dialog">
          <DialogHeader>
            <DialogTitle>Invia email di test</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Email di destinazione</Label>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tuo.indirizzo@azienda.it"
              data-testid="test-email-input"
            />
            <p className="text-xs text-muted-foreground">
              L'email verrà spedita usando il tuo account Microsoft 365 collegato.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>
              Annulla
            </Button>
            <Button onClick={sendTest} data-testid="send-test-confirm">
              Invia test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Programma invio</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Data e ora</Label>
            <Input
              type="datetime-local"
              value={scheduleValue}
              onChange={(e) => setScheduleValue(e.target.value)}
              data-testid="schedule-datetime"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>
              Annulla
            </Button>
            <Button onClick={schedule} data-testid="schedule-confirm">
              Programma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= SEND PROGRESS DIALOG ============= */}
      <Dialog
        open={sendOpen}
        onOpenChange={(o) => {
          // Only allow closing after completion
          if (!o && progress?.status !== "sent" && progress?.status !== "failed") return;
          setSendOpen(o);
        }}
      >
        <DialogContent
          className="max-w-lg"
          data-testid="send-progress-dialog"
          onEscapeKeyDown={(e) => {
            if (progress?.status !== "sent" && progress?.status !== "failed") e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (progress?.status !== "sent" && progress?.status !== "failed") e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {progress?.status === "sent" ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-primary" strokeWidth={1.75} />
                  Invio completato
                </>
              ) : progress?.status === "failed" ? (
                <>
                  <XCircle className="w-5 h-5 text-destructive" strokeWidth={1.75} />
                  Invio fallito
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 text-primary animate-pulse" strokeWidth={1.75} />
                  Invio in corso…
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">
                  {progress?.processed || 0} di {progress?.total || 0} destinatari
                </span>
                <span className="font-heading text-xl tabular-nums" data-testid="progress-percent">
                  {progress?.percent || 0}%
                </span>
              </div>
              <Progress
                value={progress?.percent || 0}
                className="h-2"
                data-testid="progress-bar"
              />
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="border border-border p-3">
                <div className="font-heading text-2xl text-primary" data-testid="progress-sent">
                  {progress?.sent || 0}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  Inviate
                </div>
              </div>
              <div className="border border-border p-3">
                <div className="font-heading text-2xl" data-testid="progress-failed">
                  {progress?.failed || 0}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  Fallite
                </div>
              </div>
              <div className="border border-border p-3">
                <div className="font-heading text-2xl text-muted-foreground">
                  {Math.max(0, (progress?.total || 0) - (progress?.processed || 0))}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                  In coda
                </div>
              </div>
            </div>

            {progress?.status === "sending" && (
              <p className="text-xs text-muted-foreground text-center">
                Puoi chiudere questa finestra: l'invio continua in background.
                Riapri la campagna per rivedere il progresso.
              </p>
            )}
            {progress?.status === "sent" && (
              <p className="text-sm text-center text-muted-foreground">
                Tutti i messaggi sono stati processati. Vai al report per vedere aperture e click.
              </p>
            )}
            {progress?.status === "failed" && progress?.error && (
              <div className="border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {progress.error}
              </div>
            )}
          </div>

          <DialogFooter>
            {progress?.status === "sending" ? (
              <Button
                variant="outline"
                onClick={() => setSendOpen(false)}
                data-testid="progress-close-bg"
              >
                Chiudi (l'invio continua)
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setSendOpen(false)}
                  data-testid="progress-close"
                >
                  Chiudi
                </Button>
                <Button
                  onClick={() => {
                    setSendOpen(false);
                    navigate(`/campagne/${id}/report`);
                  }}
                  data-testid="progress-view-report"
                >
                  Vai al report
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ============= RESEND CONFIRMATIONS (2-step) ============= */}
      <Dialog open={resendStep === 1} onOpenChange={(o) => !o && setResendStep(0)}>
        <DialogContent data-testid="resend-confirm-1">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-destructive">⚠</span> Attenzione: campagna già inviata
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Questa campagna <strong>è già stata spedita</strong>
              {prevSendInfo.sent_at && (
                <> il{" "}
                  <strong>
                    {new Date(prevSendInfo.sent_at).toLocaleString("it-IT", { timeZone: "Europe/Rome" })}
                  </strong>
                </>
              )}
              {" "}a <strong>{prevSendInfo.recipients}</strong> destinatari
              ({prevSendInfo.sent} consegnate).
            </p>
            <p>
              Rinviarla farà arrivare una <strong>nuova email</strong> a tutti i contatti
              attualmente iscritti nelle liste selezionate — anche a chi l'ha già ricevuta.
              I contatori delle statistiche verranno azzerati.
            </p>
            <p className="text-muted-foreground">
              Sei sicuro di voler procedere con il reinvio?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendStep(0)} data-testid="resend-cancel-1">
              Annulla
            </Button>
            <Button variant="destructive" onClick={() => setResendStep(2)} data-testid="resend-confirm-1-btn">
              Sì, procedi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resendStep === 2} onOpenChange={(o) => !o && setResendStep(0)}>
        <DialogContent data-testid="resend-confirm-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-destructive">⚠⚠</span> Conferma definitiva
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="border border-destructive/30 bg-destructive/5 p-4">
              <p className="font-medium mb-1">Sto per inviare di nuovo questa campagna a {recipientCount} destinatari.</p>
              <p className="text-muted-foreground text-xs">
                Verrà rimosso lo storico invii precedente e i contatori verranno azzerati.
                Le aperture/click ricevuti dopo il primo invio non saranno più associabili al nuovo.
              </p>
            </div>
            <p>
              Confermi <strong>definitivamente</strong> il reinvio?
              Questa azione non può essere annullata.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendStep(0)} data-testid="resend-cancel-2">
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={doResendCampaign}
              data-testid="resend-confirm-2-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Sì, rimanda ora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
